import { useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Globe, Radar, Settings } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import { EmptyState, ErrorState, PageHeader, PageSkeleton } from "../components/Ui";
import { NoClient } from "../components/Workflow";
import BreakdownPanel from "../components/web/BreakdownPanel";
import FilterBar from "../components/web/FilterBar";
import LiveBadge from "../components/web/LiveBadge";
import MetricStrip from "../components/web/MetricStrip";
import { resolveRange, todayIn } from "../date-range";
import RangePicker from "../components/web/RangePicker";
import SessionsPanel from "../components/web/SessionsPanel";
import AttributionPanel from "../components/web/AttributionPanel";
import SignalsPanel from "../components/web/SignalsPanel";
import SiteSettingsDialog from "../components/web/SiteSettingsDialog";
import UptimePanel from "../components/web/UptimePanel";
import TrafficChart from "../components/web/TrafficChart";
import WorldMap from "../components/web/WorldMap";
import {
  attributionQuery,
  filtersFromSearch,
  isKnownDim,
  pickFilter,
  readAttribution,
  statsQuery,
  withParams,
  withSavedSite,
  type Attribution,
  type Breakdown,
  type Roas,
  type Session,
  type Site,
  type SiteSettings,
  type Stats,
} from "../web-analytics";

const siteName = (s: Site) => s.label || s.domains[0] || `Site ${s.id}`;

function Workspace({ clientId, clientName }: { clientId: number; clientName: string }) {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  // The PATCH allows the same two roles.
  const canEdit = user?.role === "superadmin" || user?.role === "exec";
  const [settingsOpen, setSettingsOpen] = useState(false);
  // A plain push, so the back button steps through ranges and filters.
  const go = (next: string) => setParams(new URLSearchParams(next));
  const sites = useApi<Site[]>(`/api/web/${clientId}/sites`);
  const list = sites.data ?? [];
  const site = list.find((s) => String(s.id) === params.get("site")) ?? list.find((s) => !s.hidden) ?? list[0];

  // The chosen site is always named explicitly, so its zone and the numbers agree.
  const query = site
    ? statsQuery(new URLSearchParams(withParams(params, { site: String(site.id) })), site.time_zone, (key) => resolveRange({ key }, todayIn(site.time_zone), "today"))
    : null;
  const stats = useApi<Stats>(site && site.last_event_at ? `/api/web/${clientId}/stats?${query}` : null);
  // The map shades every country, not the top ten, so it gets its own longer page (as in Sonar).
  const countries = useApi<Breakdown>(site && site.last_event_at ? `/api/web/${clientId}/breakdown/country?${query}&limit=250` : null);
  const sessions = useApi<{ sessions: Session[] }>(site && site.last_event_at ? `/api/web/${clientId}/sessions?${query}&limit=50` : null);
  // Same idea for the sessions table. The payload does not name its site, so
  // the site is noted when a NEW payload lands (a fetch is always for the site on screen).
  const lastSessions = useRef<{ data: { sessions: Session[] }; siteId: number } | null>(null);
  if (sessions.data && site && sessions.data !== lastSessions.current?.data) lastSessions.current = { data: sessions.data, siteId: site.id };
  // A new range or filter is a new path, which clears `data`; keep the last
  // numbers on screen (dimmed) until the new ones land instead of flashing a skeleton.
  const last = useRef<Stats | null>(null);
  if (stats.data) last.current = stats.data;
  const shown = stats.data ?? (stats.loading && last.current?.site.id === site?.id ? last.current : null);

  // Model and window are URL state too; both calls share them, so the tables agree.
  const view = readAttribution(params);
  const attrQs = query ? attributionQuery(query, view) : null;
  const attribution = useApi<Attribution>(site && site.last_event_at && attrQs ? `/api/web/${clientId}/attribution?${attrQs}` : null);
  const roas = useApi<Roas>(site && site.last_event_at && attrQs ? `/api/web/${clientId}/roas?${attrQs}` : null);
  // Same keep-the-last-numbers rule as the strip, per site, while a new model or window loads.
  const lastAttr = useRef<{ data: Attribution; siteId: number } | null>(null);
  const lastRoas = useRef<{ data: Roas; siteId: number } | null>(null);
  if (attribution.data && site) lastAttr.current = { data: attribution.data, siteId: site.id };
  if (roas.data && site) lastRoas.current = { data: roas.data, siteId: site.id };
  const kept = <T,>(ref: { data: T; siteId: number } | null) => (ref && ref.siteId === site?.id ? ref.data : null);
  const shownAttr = attribution.data ?? (attribution.loading ? kept(lastAttr.current) : null);
  const shownRoas = roas.data ?? (roas.loading ? kept(lastRoas.current) : null);

  const filters = filtersFromSearch(params);
  const description = site
    ? `Visits, traffic sources and leads for ${site.domains[0] || siteName(site)}.`
    : `Visits, traffic sources and leads from the funnels NOLA tracks for ${clientName}.`;
  // Straight to the Tracking script panel (it scrolls into view), not the top of a long page.
  const trackingLink = (text: string) => <Link className="button" to={`/tracking?client_id=${clientId}#tracking-script`}>{text}</Link>;

  const header = (actions?: ReactNode) => (
    <PageHeader eyebrow="Performance" title="Website analytics" description={description} actions={actions} />
  );

  if (sites.loading) return <>{header()}<PageSkeleton cards={5} /></>;
  if (sites.error) return <>{header()}<ErrorState message={sites.error} onRetry={sites.reload} /></>;
  if (!site) {
    return (
      <>
        {header()}
        <section className="panel wa-empty">
          <EmptyState
            icon={<Radar aria-hidden="true" />}
            title="No funnel is tracked yet"
            description="Add the funnel's domains on the Tracking page and paste the snippet in its header. Visits show here within seconds."
            action={trackingLink("Set up the tracking script")}
          />
        </section>
      </>
    );
  }

  const unknown = filters.some((f) => !isKnownDim(f.dim));
  const emptyReason = unknown
    ? "A filter in this address matches nothing. Remove it to see traffic."
    : filters.length
      ? "No visits match these filters in this range."
      : "No visits in this range.";

  const actions = (
    <div className="wa-actions">
      {list.length >= 2 && (
        <label className="select-control wa-site">
          <Globe aria-hidden="true" />
          <span className="sr-only">Website</span>
          <select value={site.id} onChange={(e) => go(withParams(params, { site: e.target.value }))}>
            {list.map((s) => <option key={s.id} value={s.id}>{siteName(s)}{s.hidden ? " (hidden)" : ""}</option>)}
          </select>
        </label>
      )}
      <LiveBadge
        clientId={clientId}
        siteId={site.id}
        filters={filters.map((f) => `${f.dim}:${f.value}`)}
        initial={site.live}
      />
      <RangePicker search={params} go={go} zone={site.time_zone} />
      {canEdit && (
        <button type="button" className="button secondary wa-settings-button" onClick={() => setSettingsOpen(true)}>
          <Settings aria-hidden="true" /> Settings
        </button>
      )}
    </div>
  );

  // A zone or money-page change re-dates or re-counts every number on the page.
  // The saved row goes into the list first, so reopening Settings before the
  // reload lands shows what was saved.
  const saved = (updated: SiteSettings) => {
    setSettingsOpen(false);
    sites.setData((list) => withSavedSite(list, updated));
    sites.reload();
    stats.reload();
    countries.reload();
    sessions.reload();
    attribution.reload();
    roas.reload();
  };

  return (
    <>
      {header(actions)}
      {settingsOpen && <SiteSettingsDialog site={site} clientId={clientId} onClose={() => setSettingsOpen(false)} onSaved={saved} />}
      <div className={stats.loading && shown ? "wa-page wa-busy" : "wa-page"} aria-busy={stats.loading}>
        <FilterBar search={params} />
        {!site.last_event_at ? (
          <section className="panel wa-empty">
            <EmptyState
              icon={<Radar aria-hidden="true" />}
              title="Waiting for the first visit"
              description={`${site.domains[0] || siteName(site)} has not sent a page view yet. Check the snippet is pasted in the funnel's header. Visits show here within seconds.`}
              action={trackingLink("Check the tracking script")}
            />
          </section>
        ) : stats.error ? (
          <ErrorState message={stats.error} onRetry={stats.reload} />
        ) : !shown ? (
          <PageSkeleton cards={5} />
        ) : (
          <>
            <MetricStrip totals={shown.totals} />
            <TrafficChart stats={shown} emptyReason={emptyReason} />
            <SignalsPanel
              stats={shown}
              filters={filters}
              moneyPaths={shown.site.money_paths}
              onPick={(dim, value) => go(pickFilter(params, dim, value))}
              onSetUp={canEdit ? () => setSettingsOpen(true) : undefined}
            />
            {attribution.error ? (
              <ErrorState message={attribution.error} onRetry={attribution.reload} />
            ) : shownAttr ? (
              <div className={attribution.loading || roas.loading ? "wa-busy" : undefined} aria-busy={attribution.loading || roas.loading}>
                <AttributionPanel
                  attribution={shownAttr}
                  roas={roas.error ? null : shownRoas}
                  view={view}
                  onView={(next) => go(withParams(params, { model: next.model, window: String(next.window) }))}
                  visitors={shown.totals.visitors}
                  filtered={filters.length > 0}
                />
                {roas.error && <p className="wa-bd-error" role="alert">Ad spend could not load: {roas.error}</p>}
              </div>
            ) : (
              <PageSkeleton cards={1} />
            )}
            <Breakdowns
              stats={shown}
              mapRows={countries.data?.rows ?? shown.breakdowns.country ?? []}
              search={params}
              queryKey={query ?? ""}
              // The strip and chart are dimmed on the previous numbers until this is false.
              busy={stats.loading || stats.refreshing || shown !== stats.data}
              onPick={(dim, value) => go(pickFilter(params, dim, value))}
              loadMore={(dim, offset) => api<Breakdown>(`/api/web/${clientId}/breakdown/${encodeURIComponent(dim)}?${query}&offset=${offset}&limit=20`)}
            />
            <SessionsPanel
              sessions={sessions.data?.sessions ?? (sessions.loading && lastSessions.current?.siteId === site.id ? lastSessions.current.data.sessions : null)}
              timeZone={shown.site.time_zone}
              error={sessions.error}
            />
          </>
        )}
        {/* Uptime does not depend on visits, so it shows before the first one too.
            Keyed on the saved settings, so a monitor change refetches it. */}
        <UptimePanel key={`${site.id}|${site.monitor}|${site.monitor_url}`} clientId={clientId} siteId={site.id} timeZone={site.time_zone} />
      </div>
    </>
  );
}

type BreakdownsProps = {
  stats: Stats;
  mapRows: Breakdown["rows"];
  search: URLSearchParams;
  queryKey: string;
  busy: boolean;
  onPick: (dim: string, value: string) => void;
  loadMore: (dim: string, offset: number) => Promise<Breakdown>;
};

/** The breakdown panels. Dim names are the API's (nola_web_breakdown's `dims` keys). */
function Breakdowns({ stats, mapRows, search, queryKey, busy, onPick, loadMore }: BreakdownsProps) {
  const filters = filtersFromSearch(search);
  const common = { rows: stats.breakdowns, filters, onPick, loadMore, queryKey, busy };
  return (
    <section className="wa-breakdowns" aria-labelledby="wa-breakdowns-heading">
      <div className="wa-section-head">
        <h2 id="wa-breakdowns-heading">Traffic details</h2>
        <p>Click any row to filter the whole page by it.</p>
      </div>
      <div className="wa-bd-grid">
        <BreakdownPanel {...common} title="Pages" caption="Most visited" dims={[{ dim: "path", label: "Page" }]} />
        {/* Two short lists beside the long one, so the first row does not leave a hole. */}
        <div className="wa-bd-stack">
          <BreakdownPanel {...common} title="Sources" caption="Referring sites; direct visits are not listed" dims={[{ dim: "ref", label: "Referrer" }]} />
          <BreakdownPanel {...common} title="Ad clicks" caption="Visits that came from an ad click" dims={[{ dim: "click_source", label: "Click source" }]} />
        </div>
        <BreakdownPanel
          {...common}
          title="Campaigns"
          caption="UTM tags"
          dims={[
            { dim: "src", label: "Source" },
            { dim: "medium", label: "Medium" },
            { dim: "campaign", label: "Campaign" },
            { dim: "utm_content", label: "Ad" },
            { dim: "utm_term", label: "Keyword" },
          ]}
        />
        <BreakdownPanel
          {...common}
          title="Devices"
          caption="Visitor technology"
          dims={[
            { dim: "device", label: "Device" },
            { dim: "browser", label: "Browser" },
            { dim: "os", label: "OS" },
          ]}
        />
        <BreakdownPanel
          {...common}
          className="wa-bd-countries"
          title="Countries"
          caption="Where visitors are"
          dims={[{ dim: "country", label: "Country" }]}
          visual={<div className="wa-map-frame"><WorldMap rows={mapRows} /></div>}
        />
      </div>
    </section>
  );
}

export default function WebAnalytics() {
  const { clientId, client } = useClientSelection();
  if (!clientId) {
    return (
      <>
        <PageHeader eyebrow="Performance" title="Website analytics" />
        <NoClient />
      </>
    );
  }
  return <Workspace key={clientId} clientId={clientId} clientName={client?.name || "this client"} />;
}
